/**
 * useCountUp — animação suave de contagem de números, com easing.
 * Útil pra KPIs que aparecem subindo do 0 ao valor final.
 */
import { useEffect, useRef, useState } from "react";

export interface UseCountUpOptions {
  duration?: number; // ms
  decimals?: number;
  prefix?: string;
  suffix?: string;
  /** Pula animação quando true (initial render only) */
  enabled?: boolean;
}

function easeOutExpo(t: number): number {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

export function useCountUp(
  end: number | string | null | undefined,
  opts: UseCountUpOptions = {},
): { value: number; display: string; isAnimating: boolean } {
  const { duration = 1200, decimals = 0, prefix = "", suffix = "", enabled = true } = opts;
  const numericEnd = typeof end === "string" ? parseFloat(end) : end ?? 0;
  const [value, setValue] = useState(numericEnd);
  const [isAnimating, setIsAnimating] = useState(false);
  const startRef = useRef<number | null>(null);
  const fromRef = useRef(value);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || Number.isNaN(numericEnd)) {
      setValue(numericEnd);
      return;
    }

    fromRef.current = value;
    startRef.current = null;
    setIsAnimating(true);

    const tick = (ts: number) => {
      if (startRef.current === null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(1, elapsed / duration);
      const eased = easeOutExpo(t);
      const current = fromRef.current + (numericEnd - fromRef.current) * eased;
      setValue(current);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setIsAnimating(false);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numericEnd, duration, enabled]);

  const formatted = Number.isFinite(value)
    ? value.toLocaleString("pt-BR", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    : "—";

  return {
    value,
    display: `${prefix}${formatted}${suffix}`,
    isAnimating,
  };
}

/** Pra moeda BRL */
export function useCountUpBRL(end: number | string | null | undefined, duration = 1200) {
  return useCountUp(end, {
    duration,
    decimals: 2,
    prefix: "R$ ",
  });
}
